import { useId, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { AlertTriangle, ChevronDown } from 'lucide-react'
import type { Verification } from '@/api/types'
import { ease, spring } from '@/lib/motion'

type Props = { verification: Verification | null | undefined; verifying: boolean }

/** A check mark that draws itself. */
function DrawnCheck() {
  return (
    <motion.span
      initial={{ scale: 0.6, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={spring.snappy}
      className="flex size-4 items-center justify-center rounded-full bg-success text-white dark:text-black"
    >
      <svg viewBox="0 0 16 16" className="size-2.5" fill="none" aria-hidden>
        <motion.path
          d="M3.5 8.5l3 3 6-7"
          stroke="currentColor"
          strokeWidth={2.4}
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.45, ease: ease.out, delay: 0.12 }}
        />
      </svg>
    </motion.span>
  )
}

export function VerificationBadge({ verification, verifying }: Props) {
  const [open, setOpen] = useState(false)
  const issuesId = useId()

  if (!verification) {
    if (!verifying) return null
    return (
      <p className="mt-4 inline-flex items-center gap-2 text-xs" aria-live="off">
        <span className="ai-orb inline-block size-3" aria-hidden />
        <span className="shimmer-text font-medium">Checking the answer against its sources…</span>
      </p>
    )
  }

  if (verification.status === 'supported') {
    return (
      <motion.p
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={spring.smooth}
        className="mt-4 inline-flex items-center gap-2 text-xs font-medium text-success"
      >
        <DrawnCheck />
        Checked against sources
      </motion.p>
    )
  }

  if (verification.status !== 'partially_supported' && verification.status !== 'unsupported') return null

  const issues = verification.issues ?? []
  const label = 'Some statements need checking'
  if (!issues.length) {
    return (
      <p className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-warning motion-safe:animate-fade-up">
        <AlertTriangle className="size-3.5" aria-hidden />
        {label}
      </p>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={spring.smooth}
      className="mt-4"
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={issuesId}
        onClick={() => setOpen((v) => !v)}
        className="pressable -ml-1.5 inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs font-medium text-warning hover:bg-warning-soft"
      >
        <AlertTriangle className="size-3.5" aria-hidden />
        {label}
        <span className="font-normal">({issues.length})</span>
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={spring.snappy} className="inline-flex">
          <ChevronDown className="size-3.5" aria-hidden />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={spring.smooth}
            className="overflow-hidden"
          >
            <ul
              id={issuesId}
              className="mt-2 space-y-2.5 rounded-2xl bg-warning-soft p-3.5 text-sm shadow-[inset_0_0_0_0.5px_rgb(var(--warning)/0.3)]"
            >
              {issues.map((issue, index) => (
                <li key={index}>
                  <p className="font-medium text-ink">&ldquo;{issue.claim}&rdquo;</p>
                  <p className="mt-0.5 text-ink-muted">{issue.problem}</p>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}
